import type { PracticeSchedule } from "../../types";

/* A month grid of practice history. Solid green = a day you practiced; a dashed
   accent ring = a day your plan calls for (future only). No streaks, no guilt —
   missed planned days simply aren't drawn as failures. */

const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"]; // Monday-first
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const mondayIndex = (d: Date) => (d.getDay() + 6) % 7;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

interface Props {
  month: Date; // any date within the month to render
  practicedDays: Set<string>; // dayKey set of days with a completed session
  schedule: PracticeSchedule;
  today: Date;
  onPrev: () => void;
  onNext: () => void;
  onSelectDay?: (date: Date) => void;
  selectedKey?: string | null;
}

function Chevron({ dir }: { dir: "left" | "right" }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points={dir === "left" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"} />
    </svg>
  );
}

export default function MonthCalendar({ month, practicedDays, schedule, today, onPrev, onNext, onSelectDay, selectedKey }: Props) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const lead = mondayIndex(first);
  const todayStart = startOfDay(today);
  const todayKey = dayKey(today);

  const cells: ({ date: Date; key: string } | null)[] = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(month.getFullYear(), month.getMonth(), d);
    cells.push({ date, key: dayKey(date) });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-semibold text-[1.05rem] text-ink">
          {MONTHS[month.getMonth()]} {month.getFullYear()}
        </h3>
        <div className="flex items-center gap-1">
          <button type="button" onClick={onPrev} aria-label="Previous month"
            className="w-8 h-8 grid place-items-center rounded-full text-text-tertiary hover:text-ink hover:bg-surface2 transition-colors cursor-pointer">
            <Chevron dir="left" />
          </button>
          <button type="button" onClick={onNext} aria-label="Next month"
            className="w-8 h-8 grid place-items-center rounded-full text-text-tertiary hover:text-ink hover:bg-surface2 transition-colors cursor-pointer">
            <Chevron dir="right" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5 mb-2">
        {WEEKDAY_LABELS.map((l, i) => (
          <div key={i} className="text-center text-[0.62rem] uppercase tracking-[0.12em] text-text-tertiary font-semibold">{l}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((cell, i) => {
          if (!cell) return <div key={`b-${i}`} />;
          const { date, key } = cell;
          const practiced = practicedDays.has(key);
          const isToday = key === todayKey;
          const isFutureOrToday = startOfDay(date).getTime() >= todayStart.getTime();
          const planned = !practiced && isFutureOrToday && schedule.days.includes(mondayIndex(date));
          const selected = selectedKey === key;
          const clickable = !!onSelectDay && practiced;

          return (
            <button
              key={key}
              type="button"
              onClick={clickable ? () => onSelectDay!(date) : undefined}
              aria-current={isToday ? "date" : undefined}
              className={[
                "aspect-square rounded-[0.6rem] grid place-items-center text-[0.8rem] tabular-nums transition-colors relative",
                practiced ? "bg-success text-white font-semibold" : "bg-surface2/40 text-text-secondary",
                planned ? "border-2 border-dashed border-accent/55" : isToday ? "border-2 border-accent" : "border border-transparent",
                selected ? "ring-2 ring-success ring-offset-1 ring-offset-surface" : "",
                clickable ? "cursor-pointer hover:brightness-110" : "cursor-default",
              ].join(" ")}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      {/* legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-5 text-[0.72rem] text-text-tertiary">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-[0.3rem] bg-success inline-block" /> Practiced
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-[0.3rem] border-2 border-dashed border-accent/55 inline-block" /> Planned
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-[0.3rem] border-2 border-accent inline-block" /> Today
        </span>
      </div>
    </div>
  );
}
